import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

/* ─── Access Tiers ──────────────────────────────────────────── */
export const useAccessTiers = (artistId) => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!artistId) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('d2f_access_tiers')
      .select('*')
      .eq('artist_id', artistId)
      .order('rank', { ascending: true });
    setTiers(data || []);
    setLoading(false);
  }, [artistId]);

  useEffect(() => { fetch(); }, [fetch]);

  const createTier = async (payload) => {
    const { data, error } = await supabase
      .from('d2f_access_tiers')
      .insert({ ...payload, artist_id: artistId })
      .select().single();
    if (!error) await fetch();
    return { data, error };
  };

  const updateTier = async (id, updates) => {
    const { error } = await supabase.from('d2f_access_tiers').update(updates).eq('id', id);
    if (!error) await fetch();
    return { error };
  };

  const deleteTier = async (id) => {
    const { error } = await supabase.from('d2f_access_tiers').delete().eq('id', id);
    if (!error) await fetch();
    return { error };
  };

  return { tiers, loading, refetch: fetch, createTier, updateTier, deleteTier };
};

/* ─── Access Rules ──────────────────────────────────────────── */
export const useContentAccessRules = (contentId, contentType = 'exclusive') => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!contentId) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('d2f_content_access_rules')
      .select('*, d2f_access_tiers(*)')
      .eq('content_id', contentId)
      .eq('content_type', contentType);
    setRules(data || []);
    setLoading(false);
  }, [contentId, contentType]);

  useEffect(() => { fetch(); }, [fetch]);

  const upsertRule = async (payload) => {
    const { data, error } = await supabase
      .from('d2f_content_access_rules')
      .upsert({ ...payload, content_id: contentId, content_type: contentType }, { onConflict: 'content_id,content_type' })
      .select().single();
    if (!error) await fetch();
    return { data, error };
  };

  const deleteRule = async (id) => {
    const { error } = await supabase.from('d2f_content_access_rules').delete().eq('id', id);
    if (!error) await fetch();
    return { error };
  };

  return { rules, loading, refetch: fetch, upsertRule, deleteRule };
};

/* ─── Access Sessions (fan-side) ────────────────────────────── */
export const useAccessSessions = (userId) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('d2f_access_sessions')
      .select('*, d2f_access_tiers(name, color)')
      .eq('user_id', userId)
      .is('revoked_at', null);
    setSessions(data || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetch(); }, [fetch]);

  const isActive = (contentId, contentType = 'exclusive') => {
    return sessions.some(s =>
      s.content_id === contentId &&
      s.content_type === contentType &&
      !s.revoked_at &&
      (!s.expires_at || new Date(s.expires_at) > new Date())
    );
  };

  const getSession = (contentId, contentType = 'exclusive') =>
    sessions.find(s => s.content_id === contentId && s.content_type === contentType);

  const grantSession = async ({ contentId, contentType = 'exclusive', tierId, amount, source = 'pwyw', expiresAt }) => {
    const { error } = await supabase
      .from('d2f_access_sessions')
      .upsert({
        user_id: userId,
        content_id: contentId,
        content_type: contentType,
        access_tier_id: tierId || null,
        amount_paid: parseFloat(amount) || 0,
        grant_source: source,
        expires_at: expiresAt || null,
        revoked_at: null,
      }, { onConflict: 'user_id,content_id,content_type' });
    if (!error) await fetch();
    return { error };
  };

  const revokeSession = async (sessionId, reason = '') => {
    const { error } = await supabase
      .from('d2f_access_sessions')
      .update({ revoked_at: new Date().toISOString(), revoked_reason: reason })
      .eq('id', sessionId);
    if (!error) await fetch();
    return { error };
  };

  return { sessions, loading, isActive, getSession, grantSession, revokeSession, refetch: fetch };
};

/* ─── Artist-side session management ───────────────────────── */
export const useArtistAccessSessions = (artistId, contentType = 'drop') => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!artistId) { setLoading(false); return; }
    setLoading(true);

    const contentTable = contentType === 'drop' ? 'd2f_drops' : 'd2f_exclusive_content';
    const { data: contentRows } = await supabase
      .from(contentTable)
      .select('id')
      .eq('artist_id', artistId);

    const ids = (contentRows || []).map(r => r.id);
    if (!ids.length) { setSessions([]); setLoading(false); return; }

    const { data } = await supabase
      .from('d2f_access_sessions')
      .select('*, d2f_access_tiers(name, color)')
      .in('content_id', ids)
      .eq('content_type', contentType)
      .order('granted_at', { ascending: false });

    setSessions(data || []);
    setLoading(false);
  }, [artistId, contentType]);

  useEffect(() => { fetch(); }, [fetch]);

  const revokeSession = async (sessionId, reason = '') => {
    const { error } = await supabase
      .from('d2f_access_sessions')
      .update({ revoked_at: new Date().toISOString(), revoked_reason: reason })
      .eq('id', sessionId);
    if (!error) await fetch();
    return { error };
  };

  return { sessions, loading, revokeSession, refetch: fetch };
};